import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const COLORS = {
  HEADER: '4F81BD',
  SUBHEADER: 'DCE6F1',
  BRANCH: 'F79646',
  TOTAL: '92D050',
  GRAND_TOTAL: '00B050',
  LEDGER: 'FFFFFF',
  ALTERNATE: 'F2F2F2',
  TEXT: {
    DARK: '000000',
    LIGHT: 'FFFFFF'
  }
};

export async function excelExportMultipleTables({
                                                  tables,
                                                  fileName = "Financial_Report.xlsx",
                                                  totalRowStyle = {
                                                    fillColor: COLORS.TOTAL,
                                                    fontColor: COLORS.TEXT.DARK,
                                                    bold: true
                                                  }
                                                }) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Financial System';
  workbook.lastModifiedBy = 'Auto Report Generator';

  // Add metadata for Excel compatibility
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.lastPrinted = new Date();
  workbook.properties = {
    date1904: false // Use 1900 date system
  };

  const sheet = workbook.addWorksheet("Financial Report", {
    views: [{
      state: 'frozen',
      xSplit: 0,
      ySplit: 2,
      topLeftCell: 'A3',
      activeCell: 'A3'
    }],
    properties: {
      defaultColWidth: 15,
      defaultRowHeight: 20
    }
  });

  // Set default document styles
  workbook.styles = {
    default: {
      font: {
        name: 'Calibri',
        size: 11,
        family: 2 // Swiss (Arial-like)
      }
    }
  };

  let currentRow = 1;
  const allBranchNames = [...new Set(tables.flatMap(table =>
      table.data.map(item => item.branchName)
  ))];

  // Set explicit column widths (in characters)
  const colWidths = [
    12,   // Column A: Code
    70,   // Column B: Account Name
    ...allBranchNames.map(() => 15), // Branch columns
    15    // Grand Total column
  ];

  sheet.columns = colWidths.map((width, idx) => ({
    width,
    style: idx > 1 ? { numFmt: '#,##0.00' } : {}
  }));

  currentRow = await writeTable(
      sheet,
      currentRow,
      tables.flatMap(table => table.data),
      allBranchNames,
      totalRowStyle
  );

  // Enhanced page setup
  sheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9, // A4
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.75,
      bottom: 0.75,
      header: 0.3,
      footer: 0.3
    },
    horizontalCentered: true
  };

  // ===================== SUMMARY SHEET (Sheet 2) =====================
  const summarySheet = workbook.addWorksheet("Summary", {
    views: [{
      state: 'frozen',
      xSplit: 0,
      ySplit: 3,
      topLeftCell: 'A4',
      activeCell: 'A4'
    }],
    properties: {
      defaultColWidth: 20,
      defaultRowHeight: 20
    }
  });

  summarySheet.columns = [
    { width: 40 },
    { width: 22, style: { numFmt: '#,##0.00' } },
    { width: 22, style: { numFmt: '#,##0.00' } },
    { width: 25, style: { numFmt: '#,##0.00' } }
  ];

  const summaryHeaderFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.HEADER } };
  const summarySubHeaderFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.SUBHEADER } };
  const summaryBranchFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.BRANCH } };
  const summaryGrandFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.GRAND_TOTAL } };
  const thinBorder = {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } }
  };

  // Row 1
  const sRow1 = summarySheet.getRow(1);
  sRow1.getCell(1).value = "Account Ledgers";
  sRow1.getCell(2).value = "All income total";
  sRow1.getCell(3).value = "All Expenses Total";
  sRow1.getCell(4).value = "Net Result (Income - Expenses)";
  [1,2,3,4].forEach(c => {
    const cell = sRow1.getCell(c);
    cell.font = { bold: true, color: { argb: COLORS.TEXT.LIGHT } };
    cell.fill = summaryHeaderFill;
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = thinBorder;
  });
  sRow1.height = 25;

  // Row 2
  const sRow2 = summarySheet.getRow(2);
  sRow2.getCell(1).value = "Select branch names";
  sRow2.getCell(2).value = "Amount";
  sRow2.getCell(3).value = "Amount";
  sRow2.getCell(4).value = "Amount";
  [1,2,3,4].forEach(c => {
    const cell = sRow2.getCell(c);
    cell.font = { bold: true, color: { argb: COLORS.TEXT.DARK } };
    cell.fill = summaryBranchFill;
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = thinBorder;
  });
  sRow2.height = 20;

  // Compute income/expense per branch from raw data
  const branchIncomeMap = {};
  const branchExpenseMap = {};
  allBranchNames.forEach(b => { branchIncomeMap[b] = 0; branchExpenseMap[b] = 0; });

  tables.flatMap(table => table.data).forEach(item => {
    const code = parseInt(item.ledgerCode, 10);
    if (isNaN(code)) return;
    const branch = item.branchName;
    if (!branch) return;
    if (branchIncomeMap[branch] === undefined) { branchIncomeMap[branch] = 0; branchExpenseMap[branch] = 0; }
    const amt = Number(item.amount || 0);
    if (code >= 1001 && code <= 1999) {
      branchIncomeMap[branch] += amt;
    } else if (code >= 2001 && code <= 2999) {
      branchExpenseMap[branch] += amt;
    }
  });

  let sRowIdx = 3;
  allBranchNames.forEach(branch => {
    const row = summarySheet.getRow(sRowIdx++);
    const income = branchIncomeMap[branch] || 0;
    const expense = branchExpenseMap[branch] || 0;
    const net = income - expense;

    row.getCell(1).value = branch;
    row.getCell(2).value = income;
    row.getCell(3).value = expense;
    row.getCell(4).value = net;

    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    row.getCell(1).font = { bold: true };
    row.getCell(1).fill = summarySubHeaderFill;

    [2,3,4].forEach(c => {
      row.getCell(c).numFmt = '#,##0.00';
      row.getCell(c).alignment = { vertical: 'middle', horizontal: 'right' };
    });

    if (net >= 0) {
      row.getCell(4).font = { bold: true, color: { argb: 'FF008000' } };
    } else {
      row.getCell(4).font = { bold: true, color: { argb: 'FFFF0000' } };
    }

    [1,2,3,4].forEach(c => { row.getCell(c).border = thinBorder; });
  });

  // Grand Total
  const grandTotalIncome = Object.values(branchIncomeMap).reduce((s,v)=>s+(v||0),0);
  const grandTotalExpense = Object.values(branchExpenseMap).reduce((s,v)=>s+(v||0),0);
  const grandTotalNet = grandTotalIncome - grandTotalExpense;

  const sGrandRow = summarySheet.getRow(sRowIdx);
  sGrandRow.getCell(1).value = "GRAND TOTAL";
  sGrandRow.getCell(2).value = grandTotalIncome;
  sGrandRow.getCell(3).value = grandTotalExpense;
  sGrandRow.getCell(4).value = grandTotalNet;

  sGrandRow.font = { bold: true, color: { argb: COLORS.TEXT.LIGHT } };
  sGrandRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  [2,3,4].forEach(c => {
    sGrandRow.getCell(c).numFmt = '#,##0.00';
    sGrandRow.getCell(c).alignment = { vertical: 'middle', horizontal: 'right' };
  });
  if (grandTotalNet >= 0) {
    sGrandRow.getCell(4).font = { bold: true, color: { argb: 'FF92D050' } };
  } else {
    sGrandRow.getCell(4).font = { bold: true, color: { argb: 'FFFFC7CE' } };
  }
  [1,2,3,4].forEach(c => {
    sGrandRow.getCell(c).fill = summaryGrandFill;
    sGrandRow.getCell(c).border = thinBorder;
  });
  sGrandRow.height = 22;

  // Apply borders to all used rows
  for (let r = 1; r <= sRowIdx; r++) {
    const row = summarySheet.getRow(r);
    row.eachCell({ includeEmpty: true }, cell => {
      if (!cell.border) cell.border = thinBorder;
    });
  }

  summarySheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
}

async function writeTable(
    sheet,
    startRow,
    data,
    branchNames,
    totalRowStyle
) {
  function getExcelColorStyle(colorHex) {
    return {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colorHex },
      bgColor: { argb: 'FFFFFFFF' } // Explicit background
    };
  }

  sheet.getRow(startRow).height = 25;
  sheet.getRow(startRow + 1).height = 20;

  const groupedData = {};
  data.forEach(item => {
    const headerKey = item.headerName || 'No Header';
    const subKey = item.subNumber && item.subName
        ? `${item.subNumber} ${item.subName}`
        : item.subName || 'No Subcategory';
    const ledgerKey = `${item.ledgerCode}|${item.ledgerName}`;

    if (!groupedData[headerKey]) groupedData[headerKey] = {};
    if (!groupedData[headerKey][subKey]) groupedData[headerKey][subKey] = {};

    if (!groupedData[headerKey][subKey][ledgerKey]) {
      groupedData[headerKey][subKey][ledgerKey] = {
        ledgerCode: item.ledgerCode,
        ledgerName: item.ledgerName,
        amounts: {}
      };
    }

    groupedData[headerKey][subKey][ledgerKey].amounts[item.branchName] = item.amount;
  });

  const headerRow1 = sheet.getRow(startRow);
  const headerRow2 = sheet.getRow(startRow + 1);

  headerRow1.getCell(1).value = "Account Ledgers";
  sheet.mergeCells(startRow, 1, startRow, 2);
  headerRow1.getCell(1).style = {
    font: { bold: true, color: { argb: COLORS.TEXT.LIGHT } },
    fill: getExcelColorStyle(COLORS.HEADER),
    alignment: { vertical: 'middle', horizontal: 'left' }
  };

  branchNames.forEach((branch, index) => {
    const branchCell = headerRow1.getCell(3 + index);
    const amountCell = headerRow2.getCell(3 + index);

    branchCell.value = branch;
    branchCell.style = {
      font: { bold: true, size: 9, color: { argb: COLORS.TEXT.DARK } },
      fill: getExcelColorStyle(COLORS.BRANCH),
      alignment: { vertical: 'center', horizontal: 'center', wrapText: true }
    };

    amountCell.value = "Amount";
    amountCell.style = {
      font: { bold: true },
      fill: getExcelColorStyle(COLORS.BRANCH),
      alignment: { vertical: 'center', horizontal: 'center' }
    };
  });

  const grandTotalCol = 3 + branchNames.length;
  headerRow1.getCell(grandTotalCol).value = "Grand Total";
  headerRow2.getCell(grandTotalCol).value = "Amount";

  [headerRow1.getCell(grandTotalCol), headerRow2.getCell(grandTotalCol)].forEach(cell => {
    cell.style = {
      font: { bold: true, color: { argb: COLORS.TEXT.LIGHT } },
      fill: getExcelColorStyle(COLORS.GRAND_TOTAL),
      alignment: { vertical: 'center', horizontal: 'center' }
    };
  });

  let rowIndex = startRow + 2;
  let grandTotalMap = {};
  branchNames.forEach(branch => grandTotalMap[branch] = 0);

  let incomeSubTotalRowIndices = [];
  let expenseSubTotalRowIndices = [];

  for (const [headerName, subGroups] of Object.entries(groupedData)) {
    const headerRow = sheet.getRow(rowIndex++);
    headerRow.getCell(1).value = headerName;
    sheet.mergeCells(rowIndex - 1, 1, rowIndex - 1, grandTotalCol);
    headerRow.getCell(1).style = { font: { bold: true }, fill: getExcelColorStyle(COLORS.HEADER) };

    let categorySubTotalMap = {};
    branchNames.forEach(branch => categorySubTotalMap[branch] = 0);

    const subKeys = Object.keys(subGroups);
    for (const [subName, ledgers] of Object.entries(subGroups)) {
      if (subName !== 'No Subcategory') {
        const subRow = sheet.getRow(rowIndex++);
        subRow.getCell(1).value = subName;
        sheet.mergeCells(rowIndex - 1, 1, rowIndex - 1, grandTotalCol);
        subRow.getCell(1).style = { font: { italic: true }, fill: getExcelColorStyle(COLORS.SUBHEADER) };
      }

      let subTotalMap = {};
      branchNames.forEach(branch => subTotalMap[branch] = 0);

      for (const ledger of Object.values(ledgers)) {
        const itemRow = sheet.getRow(rowIndex++);
        itemRow.getCell(1).value = ledger.ledgerCode;
        itemRow.getCell(1).numFmt = '@';
        itemRow.getCell(2).value = ledger.ledgerName;

        branchNames.forEach((branch, colIndex) => {
          const amount = ledger.amounts[branch] || 0;
          const cell = itemRow.getCell(3 + colIndex);
          cell.value = amount;
          cell.numFmt = '#,##0.00';

          subTotalMap[branch] += amount;
          categorySubTotalMap[branch] += amount;
          grandTotalMap[branch] += amount;
        });

        const ledgerTotal = Object.values(ledger.amounts).reduce((s, v) => s + (v || 0), 0);
        itemRow.getCell(grandTotalCol).value = ledgerTotal;
        itemRow.getCell(grandTotalCol).numFmt = '#,##0.00';
      }

      const subTotalRow = sheet.getRow(rowIndex++);
      subTotalRow.getCell(1).value = "Sub Total";
      branchNames.forEach((branch, colIndex) => {
        subTotalRow.getCell(3 + colIndex).value = subTotalMap[branch];
        subTotalRow.getCell(3 + colIndex).numFmt = '#,##0.00';
      });
      subTotalRow.getCell(grandTotalCol).value =
          Object.values(subTotalMap).reduce((s, v) => s + v, 0);
      subTotalRow.getCell(grandTotalCol).numFmt = '#,##0.00';

      const isIncome = Object.keys(ledgers).some(l => {
        const code = parseInt(ledgers[l].ledgerCode, 10);
        return code >= 1001 && code <= 1999;
      });
      const isExpense = Object.keys(ledgers).some(l => {
        const code = parseInt(ledgers[l].ledgerCode, 10);
        return code >= 2001 && code <= 2999;
      });

      if (isIncome) incomeSubTotalRowIndices.push(subTotalRow.number);
      if (isExpense) expenseSubTotalRowIndices.push(subTotalRow.number);
    }

    if (subKeys.length > 1) {
      const categoryTotalRow = sheet.getRow(rowIndex++);
      categoryTotalRow.getCell(1).value = "Total";
      branchNames.forEach((branch, colIndex) => {
        categoryTotalRow.getCell(3 + colIndex).value = categorySubTotalMap[branch];
        categoryTotalRow.getCell(3 + colIndex).numFmt = '#,##0.00';
      });
      categoryTotalRow.getCell(grandTotalCol).value =
          Object.values(categorySubTotalMap).reduce((s, v) => s + v, 0);
      categoryTotalRow.getCell(grandTotalCol).numFmt = '#,##0.00';
      categoryTotalRow.getCell(1).style = { font: { bold: true } };
    }
  }

  const grandTotalRow = sheet.getRow(rowIndex++);
  grandTotalRow.getCell(1).value = "Grand Total";
  branchNames.forEach((branch, colIndex) => {
    grandTotalRow.getCell(3 + colIndex).value = grandTotalMap[branch];
    grandTotalRow.getCell(3 + colIndex).numFmt = '#,##0.00';
  });
  grandTotalRow.getCell(grandTotalCol).value =
      Object.values(grandTotalMap).reduce((s, v) => s + v, 0);
  grandTotalRow.getCell(grandTotalCol).numFmt = '#,##0.00';

  const allIncomeRowIndex = rowIndex++;
  const allIncomeRow = sheet.getRow(allIncomeRowIndex);
  sheet.mergeCells(allIncomeRowIndex, 1, allIncomeRowIndex, 2);
  allIncomeRow.getCell(1).value = "All Income Total";
  allIncomeRow.getCell(1).style = { font: { bold: true }, fill: getExcelColorStyle(COLORS.HEADER) };

  const allExpensesRowIndex = rowIndex++;
  const allExpensesRow = sheet.getRow(allExpensesRowIndex);
  sheet.mergeCells(allExpensesRowIndex, 1, allExpensesRowIndex, 2);
  allExpensesRow.getCell(1).value = "All Expenses Total";
  allExpensesRow.getCell(1).style = { font: { bold: true }, fill: getExcelColorStyle(COLORS.HEADER) };

  branchNames.forEach((_, colIndex) => {
    const col = 3 + colIndex;

    const incomeFormula = incomeSubTotalRowIndices.map(r => `${sheet.getColumn(col).letter}${r}`).join("+") || "0";
    allIncomeRow.getCell(col).value = { formula: incomeFormula };
    allIncomeRow.getCell(col).numFmt = '#,##0.00';

    const expenseFormula = expenseSubTotalRowIndices.map(r => `${sheet.getColumn(col).letter}${r}`).join("+") || "0";
    allExpensesRow.getCell(col).value = { formula: expenseFormula };
    allExpensesRow.getCell(col).numFmt = '#,##0.00';
  });

  const netRowIndex = rowIndex++;
  const netRow = sheet.getRow(netRowIndex);
  sheet.mergeCells(netRowIndex, 1, netRowIndex, 2);
  netRow.getCell(1).value = "Net Result (Income - Expenses)";
  netRow.getCell(1).style = { font: { bold: true }, fill: getExcelColorStyle(COLORS.TOTAL) };

  branchNames.forEach((_, colIndex) => {
    const col = 3 + colIndex;
    const cellAddress = `${sheet.getColumn(col).letter}${netRowIndex}`;
    netRow.getCell(col).value = {
      formula: `${sheet.getColumn(col).letter}${allIncomeRowIndex} - ${sheet.getColumn(col).letter}${allExpensesRowIndex}`
    };
    netRow.getCell(col).numFmt = '#,##0.00';

    sheet.addConditionalFormatting({
      ref: cellAddress,
      rules: [
        {
          type: 'expression',
          formulae: [`${cellAddress}>=0`],
          style: { font: { color: { argb: 'FF008000' }, bold: true } }
        },
        {
          type: 'expression',
          formulae: [`${cellAddress}<0`],
          style: { font: { color: { argb: 'FFFF0000' }, bold: true } }
        }
      ]
    });
  });

  for (let r = startRow; r < rowIndex; r++) {
    const row = sheet.getRow(r);
    row.eachCell({ includeEmpty: true }, cell => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
  }

  for (let r = startRow + 2; r < rowIndex; r++) {
    if (r % 2 === 0) {
      const row = sheet.getRow(r);
      row.eachCell({ includeEmpty: true }, cell => {
        if (!cell.style.fill) cell.style.fill = getExcelColorStyle(COLORS.ALTERNATE);
      });
    }
  }

  return rowIndex;
}